import { useState, type FormEvent, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { signIn, useAuth, signOut } from "@/hooks/useAuth";
import type { User } from "@supabase/supabase-js";

interface Props {
  children: (ctx: { user: User; signOut: () => Promise<unknown> }) => ReactNode;
  title?: string;
  subtitle?: string;
}

export function LoginGate({
  children,
  title = "DeepDive",
  subtitle = "Iniciá sesión para continuar.",
}: Props) {
  const { loading, user } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center dot-grid-bg">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (user) {
    return <>{children({ user, signOut })}</>;
  }

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    const { error } = await signIn(email.trim(), password);
    setSubmitting(false);
    if (error) setError(error.message);
  };

  return (
    <div className="min-h-screen flex items-center justify-center dot-grid-bg p-6">
      <div className="w-full max-w-md mb-36">
        {/* Logo hero */}
        <div className="mb-8 text-center">
          <img
            src="/deepdive.png"
            alt={title}
            className="mx-auto h-64 w-auto select-none"
            draggable={false}
          />
          <p className="text-muted-foreground text-sm mt-3">{subtitle}</p>
        </div>

        {/* Login card con glow violeta sutil */}
        <div className="relative">
          <div
            aria-hidden
            className="absolute -inset-x-8 -top-8 -bottom-8 bg-[radial-gradient(ellipse_at_top,hsl(272_91%_65%_/_0.18),transparent_60%)] pointer-events-none -z-10 blur-2xl"
          />
          <div className="rounded-[var(--radius)] border border-border bg-card/80 backdrop-blur-md shadow-2xl">
            <form onSubmit={onSubmit} className="p-8 space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="username"
                  autoFocus
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Contraseña</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="h-11"
                />
              </div>

              {error && (
                <div className="text-destructive text-xs bg-destructive/10 border border-destructive/30 rounded-md px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                size="lg"
                loading={submitting}
                className="w-full"
              >
                Ingresar
              </Button>
            </form>
          </div>
        </div>

        <p className="text-center text-muted-foreground text-xs mt-6">
          Acceso restringido a administradores.
        </p>
      </div>
    </div>
  );
}
